/**
 * CSV import preview/confirm panel for protocol detail.
 */

import type { ProtocolImportPreview } from "../../api/clinicalTrialsApiTypes";

type ProtocolImportSectionProps = {
  busy: boolean;
  preview: ProtocolImportPreview | null;
  onPreview: (file: File | null) => void;
  onConfirm: () => void;
};

/**
 * Renders the CSV import controls and preview summary.
 *
 * @param props - Import section props
 * @returns Import section element
 */
export function ProtocolImportSection({
  busy,
  preview,
  onPreview,
  onConfirm,
}: ProtocolImportSectionProps) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-lg font-medium">CSV import</h2>
      <input
        type="file"
        accept=".csv,text/csv"
        disabled={busy}
        onChange={(event) => void onPreview(event.target.files?.[0] || null)}
      />
      {preview ? (
        <div className="mt-4 space-y-2 text-sm">
          <div>
            Parsed {preview.criterion_count} criteria, {preview.atom_count}{" "}
            atoms, {preview.gate_count} gates, {preview.connection_count}{" "}
            connections.
          </div>
          <div>
            Errors: {preview.errors.length} · Warnings: {preview.warnings.length}
          </div>
          {preview.errors.slice(0, 5).map((item) => (
            <div key={item.code + item.message} className="text-red-600">
              {item.message}
            </div>
          ))}
          <button
            className="rounded bg-slate-900 px-3 py-1.5 text-white disabled:opacity-50"
            disabled={busy || preview.errors.length > 0}
            onClick={() => void onConfirm()}
          >
            Confirm import
          </button>
        </div>
      ) : null}
    </section>
  );
}
